import { FC, useMemo, useState } from 'react'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import { ReviewPlatform } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { cn } from 'src/utils/cn'
import {
  ReviewComparisonFacility,
  ReviewComparisonTopic,
} from 'src/services/reviewAnalysisApi/interfaces/reviewComparison.interface'
import styles from './topicComparison.module.scss'
import { facilityColor } from '../palette'

// Which side of the review the topics come from. The two sections are the same
// widget: the same topics, counted over praise or over complaint.
type TopicVariant = 'negative' | 'positive'

interface TopicComparisonProps {
  facilities: ReviewComparisonFacility[]
  insight: string
  // Whatever the two facilities actually collected on, which is what the tabs
  // can offer — a fixed list would show tabs with nothing behind them.
  platforms: string[]
  topics: ReviewComparisonTopic[]
  variant: TopicVariant
}

const COPY: Record<TopicVariant, { subtitle: string; title: string }> = {
  negative: {
    subtitle:
      'What guests complain about most, mentions in the selected period',
    title: 'Top Pain Points',
  },
  positive: {
    subtitle: 'What guests praise most, mentions in the selected period',
    title: 'Top Positive Topics',
  },
}

const PLATFORM_LABELS: Record<string, string> = {
  appstore: 'App Store',
  booking: 'Booking',
  expedia: 'Expedia',
  google: 'Google',
  playstore: 'Google Play',
  tripadvisor: 'Tripadvisor',
}

// The tab that adds every platform up, rather than one of its own.
const ALL_TAB = 'all'

// A bar narrower than this can't hold its own count, so it stops here and the
// number sits outside it. Nothing is read off the length below this width
// anyway — the count next to it is the reading.
const MIN_BAR_WIDTH = 44

export const TopicComparison: FC<TopicComparisonProps> = ({
  facilities,
  insight,
  platforms,
  topics,
  variant,
}) => {
  const [tab, setTab] = useState<string>(ALL_TAB)

  // Counts for the selected tab, and the largest of them: every bar in the
  // section is a share of that one number, so the two facilities are read on
  // one scale rather than each against its own best row.
  const { max, rows } = useMemo(() => {
    const counted = topics.map((topic) => ({
      label: topic.label,
      values: facilities.map((_, index) =>
        tab === ALL_TAB
          ? platforms.reduce(
              (sum, platform) => sum + (topic.values[platform]?.[index] ?? 0),
              0
            )
          : topic.values[tab]?.[index] ?? 0
      ),
    }))

    return {
      max: Math.max(1, ...counted.flatMap((topic) => topic.values)),
      rows: counted,
    }
  }, [facilities, platforms, tab, topics])

  const bar = (value: number, index: number, side: 'left' | 'right') => (
    <div className={cn(styles.barCell, styles[side])}>
      <span
        className={styles.bar}
        style={{
          background: facilityColor(index),
          width: `max(${MIN_BAR_WIDTH}px, ${(value / max) * 100}%)`,
        }}
      >
        <span className={styles.barValue}>{value.toLocaleString('en-US')}</span>
      </span>
    </div>
  )

  return (
    <section>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{COPY[variant].title}</h3>
          <p className={styles.subtitle}>{COPY[variant].subtitle}</p>
        </div>

        <div className={styles.legend}>
          {facilities.map((facility, index) => (
            <span className={styles.legendItem} key={facility.facilityId}>
              <span
                className={styles.legendDot}
                style={{ background: facilityColor(index) }}
              />
              <TruncatedTooltip text={facility.facilityName} />
            </span>
          ))}
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={cn(styles.tab, tab === ALL_TAB && styles.tabActive)}
          onClick={() => setTab(ALL_TAB)}
          type="button"
        >
          All
        </button>

        {platforms.map((platform) => {
          const Icon = getPlatformIcon(platform as ReviewPlatform)

          return (
            <button
              className={cn(styles.tab, tab === platform && styles.tabActive)}
              key={platform}
              onClick={() => setTab(platform)}
              type="button"
            >
              <Icon className={styles.tabIcon} />
              {PLATFORM_LABELS[platform] ?? platform}
            </button>
          )
        })}
      </div>

      <div className={cn(styles.panel, styles[variant])}>
        {rows.map((row) => (
          <div className={styles.row} key={row.label}>
            {bar(row.values[0], 0, 'left')}
            <span className={styles.label}>{row.label}</span>
            {bar(row.values[1], 1, 'right')}
          </div>
        ))}
      </div>

      <div className={styles.insight}>
        <LightbulbIcon className={styles.insightIcon} />
        <p className={styles.insightText}>{insight}</p>
      </div>
    </section>
  )
}
